import { createIcons, Save, Eye, CheckCircle, FileDown, Plus, Trash2, Upload, PenLine, History, RefreshCw, Send, Image as ImageIcon } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { aiReviewSlot, bindAiReviewHost } from "../components/ai-review-host.js";
import { router } from "../router/router.js";
import { AppError } from "../../shared/errors/app-error.js";
import {
  NDT_LAUDO_OPTIONS,
  NDT_SIGNATORY_ROLE_LABELS,
  isEmbeddableImage,
  type NdtLaudo,
  type NdtReportCommon,
  type NdtSignatory
} from "../../domain/ndt/ndt-report-common.js";
import { formatCivilDateBR } from "../../domain/ndt/lp-consumable.js";
import { formatProcedureLabel, type NdtMethod, type NdtProcedure } from "../../domain/ndt/ndt-procedure.js";
import { formatCatalogItemCode, formatCatalogItemLabel, type NdtCatalogItem } from "../../domain/ndt/ndt-technical-catalog.js";
import {
  canManageLpSignatories,
  filterEquipmentsByContext,
  fiscalCandidates,
  formatFileSize,
  formatInspectionReference,
  formatSignatoryOption,
  inspectorCandidates,
  missingLpContextFields,
  plhCandidatesForClient,
  staleLpContextFields,
  type LpContextSelection
} from "./lp-report-context.js";
import type { NdtReportSeed } from "../../application/services/ndt-report-service-base.js";
import "../components/document/pdf-preview.js";
import { autosaveDelayMs, imageRejection, loadOperationalSettings, operationalSettings, remainingPhotoSlots } from "../settings/operational-settings.js";
import type { EndMethodSettings } from "../../domain/settings/system-settings.js";
import { bindPlhApprovalPanel, plhApprovalPanel, PLH_REVIEW_UI_STYLES, openPlhSubmitModal, plhSubmitButton, reviewBanner, reviewEditingLocked } from "../components/plh-review-ui.js";
import { CONTEXT_LOAD_STYLES, ContextLoadTracker, loadList } from "../context/context-load.js";
import { CONTEXT_HYDRATION_STYLES, hydrateDocumentContext, hydrationBanner, type HydrationReport } from "../context/context-hydration.js";
import type { TechnicalDocumentReview, TechnicalDocumentType } from "../../domain/review/technical-document-review.js";
import { HOLD_UI_STYLES } from "../components/hold-ui.js";
import { CORRECTION_UI_STYLES } from "../components/correction-request-ui.js";
import { bindNdtEngineeringEvents, canResolveCorrections, loadNdtEngineeringState, ndtDeepLinkFocus, ndtEngineeringBanners, ndtHoldButton, ndtHoldLocked, type NdtEngineeringState } from "./ndt-engineering-integration.js";

export const EQUIPMENT_TYPE_LABELS: Record<string, string> = {
  PRESSURE_VESSEL: "Vaso de Pressão (VP)",
  STORAGE_TANK: "Tanque de Armazenamento (TQ)",
  HEAT_EXCHANGER: "Permutador de Calor (TC)",
  BOILER: "Caldeira (CD)",
  FURNACE: "Forno Industrial (FN)",
  PIPING_SYSTEM: "Sistema de Tubulação (ST)",
  PIPING_LINE: "Tubulação",
  OTHER: "Outro Equipamento"
};

/** Limite por imagem: acima disso o localStorage estoura e o rascunho deixa de persistir. */
export const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

export interface NdtReportPageConfig {
  method: NdtMethod;
  documentType: string;
  title: string;
  subtitle: string;
  routeBase: string;
  reportLabel: string;
  serviceKey: string;
  permissions: { view: string; create: string; edit: string; validate: string; generatePdf: string; approve: string; sign: string; manageSignatories: string };
  sections: Array<[string, string]>;
  /** Nº da seção do croqui/fotos/observações/signatários — só para os ids dos cards. */
  itemPrefix: string;
  /** Integra "ENVIAR PARA O PLH" (mecanismo central). Falso mantém o módulo fora do fluxo. */
  plhReview?: boolean;
}

/**
 * Base das páginas de elaboração dos relatórios de END por método (PM,
 * US-Convencional): contexto Cliente → Projeto → Unidade/Área → Equipamento
 * (→ Inspeção opcional), autosave, validação, preview = PDF, histórico,
 * croqui, fotos e signatários — o mesmo comportamento do LP, que segue com a
 * página dele. Cada método renderiza os seus cards.
 */
export abstract class NdtReportPageBase<R extends NdtReportCommon<any>> extends HTMLElement {
  protected abstract readonly config: NdtReportPageConfig;

  protected report: R | null = null;
  protected isLoading = true;
  protected loadError: string | null = null;
  protected isSaving = false;
  protected saveNotice: string | null = null;
  protected noticeKind: "info" | "ok" | "error" = "info";
  protected validationErrors: string[] | null = null;
  protected validationOk = false;

  protected professionals: any[] = [];
  protected procedures: NdtProcedure[] = [];
  protected norms: NdtCatalogItem[] = [];
  protected criteria: NdtCatalogItem[] = [];
  protected workOrders: any[] = [];
  protected subWorkOrders: any[] = [];
  protected registeredSignatories: any[] = [];
  protected signatureImages = new Map<string, string | null>();

  /**
   * Tratamento único das leituras de contexto: um 403 deixa de virar lista
   * vazia e de produzir "nenhum cadastro" com o cadastro existente.
   */
  protected readonly ctxLoad = new ContextLoadTracker();

  protected clients: any[] = [];
  protected projects: any[] = [];
  protected unitAreas: any[] = [];
  protected equipments: any[] = [];
  protected inspections: any[] = [];
  protected selection: LpContextSelection = { clientId: "", projectId: "", unitAreaId: "", equipmentId: "", inspectionId: "" };
  protected existingReport: R | null = null;
  /** Desfecho da reposição do contexto gravado — avisa o que não voltou. */
  protected hydration: HydrationReport | null = null;
  protected contextError: string | null = null;
  protected isResolvingContext = false;

  protected previewSnapshot: any | null = null;
  protected previewOpen = false;
  protected historyOpen = false;
  /** Ciclo de revisão PLH do relatório (mecanismo central) — decide botão, banner e bloqueio. */
  protected review: TechnicalDocumentReview | null = null;
  /** Painel de Engenharia: HOLD ativo e correções por item do relatório. */
  protected engineering: NdtEngineeringState = { hold: null, corrections: [] };
  protected activeSection = "01";
  protected lightbox: { open: boolean; src: string; title: string } = { open: false, src: "", title: "" };

  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  private boundFormInput = this.handleFormInput.bind(this);

  connectedCallback() {
    // INSPECT IA REVIEW: a revisão aplicou texto ⇒ relê o relatório do serviço.
    bindAiReviewHost(this, async () => {
      if (!this.report) return;
      const fresh = await this.svc.getReport(this.scopedCtx(this.report.clientId), this.report.id).catch(() => null);
      if (fresh) { this.report = fresh; this.render(); }
    });
    this.addEventListener("input", this.boundFormInput);
    this.addEventListener("change", this.boundFormInput);
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener("input", this.boundFormInput);
    this.removeEventListener("change", this.boundFormInput);
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
  }

  // ------------------------------------------------------------- HOOKS

  protected get svc(): any { return (window.app.platform as any)[this.config.serviceKey]; }
  /** Cadastros próprios do método (consumíveis, ativos de END). */
  protected abstract loadSpecific(): Promise<void>;
  /** Cards do método, na ordem das seções. */
  protected abstract renderCards(r: R, ro: boolean, canSign: boolean): string;
  /** Eventos dos cards do método (depois dos comuns). */
  protected abstract attachSpecificEvents(ro: boolean): void;
  /** Entrada de formulário própria do método; devolve true quando tratou. */
  protected abstract handleSpecificInput(target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, ds: DOMStringMap): boolean;
  /** Semente extra para criar o rascunho. */
  protected seedExtras(_equipment: any): Partial<NdtReportSeed> { return {}; }
  protected afterOpen(): Promise<void> { return Promise.resolve(); }

  // ------------------------------------------------------------ CONTEXT

  protected buildCtx(): any {
    const c = contextStore.get();
    return { activeClientId: c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  protected scopedCtx(clientId?: string): any {
    const base = this.buildCtx();
    const target = clientId ?? this.selection.clientId ?? this.report?.clientId;
    return target ? { ...base, activeClientId: target } : base;
  }

  protected async init() {
    this.isLoading = true;
    this.render();
    await loadOperationalSettings();
    const ctx = this.buildCtx();
    const id = this.getAttribute("id");
    try {
      this.professionals = await this.ctxLoad.load("Profissionais", () => window.app.platform.adminService.listProfessionals(ctx));
      if (id) {
        const existing = await this.svc.getReport(ctx, id);
        if (!existing) {
          this.loadError = `${this.config.reportLabel} não encontrado.`;
          this.isLoading = false;
          this.render();
          return;
        }
        await this.openReport(existing);
        return;
      }
      await this.loadClients();
      this.isLoading = false;
      this.render();
    } catch (e: any) {
      console.error(`[${this.config.documentType}] Falha ao carregar página`, e);
      this.loadError = this.messageOf(e, "Erro ao carregar o relatório.");
      this.isLoading = false;
      this.render();
    }
  }

  protected async openReport(report: R) {
    this.report = report;
    await this.hydrateContextFromReport();
    await Promise.all([this.loadProcedures(), this.loadTechnicalSources(), this.loadSpecific(), this.loadWorkOrdersForReport(), this.loadRegisteredSignatories(), this.loadReview()]);
    await this.loadSignatureImages();
    await this.afterOpen();
    this.isLoading = false;
    this.render();
  }

  protected async loadProcedures() {
    try {
      this.procedures = await window.app.platform.ndtProcedureService.listSelectableForReport(this.buildCtx(), this.config.method, this.report?.procedureId ?? null);
    } catch (e) {
      console.error(`[${this.config.documentType}] Falha ao carregar procedimentos`, e);
      this.procedures = [];
    }
  }

  protected async loadTechnicalSources() {
    const r = this.report;
    const ctx = this.buildCtx();
    const catalog = window.app.platform.ndtCatalogService;
    const [norms, criteria] = await Promise.all([
      catalog.listSelectableForReport(ctx, "REFERENCE_STANDARD", this.config.method, r?.referenceNormId ?? null).catch(() => []),
      catalog.listSelectableForReport(ctx, "ACCEPTANCE_CRITERIA", this.config.method, r?.acceptanceCriteriaId ?? null).catch(() => [])
    ]);
    this.norms = norms;
    this.criteria = criteria;
  }

  /**
   * HIDRATAÇÃO DO CONTEXTO DE UM RELATÓRIO JÁ GRAVADO (PM, US-Convencional
   * e qualquer método que herde desta base).
   *
   * Só `clientId` voltava. Projeto, Unidade/Área, Ativo e Inspeção ficavam
   * fora da seleção e suas listas nem eram carregadas — os seletores abriam
   * vazios apesar de o documento carregar os IDs, e o primeiro toque em
   * qualquer campo disparava a cascata, que apaga tudo abaixo.
   *
   * Repor não é trocar: nenhum nível limpa o de baixo. Cada um só perde o
   * valor quando ele próprio sumiu da sua lista, e aí a tela diz qual.
   */
  protected async hydrateContextFromReport() {
    const report = this.report;
    if (!report) return;
    const ctx = () => this.buildCtx();

    this.hydration = await hydrateDocumentContext([
      {
        label: "o Cliente",
        persistedId: report.clientId,
        load: () => loadList<any>("os Clientes", () => window.app.platform.adminService.listAuthorizedClients(ctx())),
        apply: (value, options) => { this.selection.clientId = value || String(report.clientId); this.clients = [...options]; }
      },
      {
        label: "o Projeto",
        persistedId: report.projectId,
        load: () => loadList<any>("os Projetos", () => window.app.platform.projectService.listProjects(this.selection.clientId, undefined, this.buildCtx())),
        apply: (value, options) => { this.selection.projectId = value; this.projects = [...options]; }
      },
      {
        label: "a Unidade / Área",
        persistedId: report.unitAreaId,
        load: () => loadList<any>("as Unidades / Áreas", () => window.app.platform.projectService.listUnitAreas({ projectId: this.selection.projectId, clientId: this.selection.clientId }, undefined, this.buildCtx())),
        apply: (value, options) => { this.selection.unitAreaId = value; this.unitAreas = [...options]; }
      },
      {
        label: "o Equipamento / TAG",
        persistedId: report.equipmentId,
        // Lido no Cliente DO DOCUMENTO, nunca no da sessão (CLAUDE.md §28).
        load: () => loadList<any>("os Equipamentos", () => window.app.platform.equipmentService.listEquipments(this.buildCtx(), { clientId: report.clientId })),
        apply: (value, options) => { this.selection.equipmentId = value; this.equipments = [...options]; }
      },
      {
        label: "a Inspeção",
        persistedId: report.inspectionId,
        load: () => loadList<any>("as Inspeções", () => this.selection.equipmentId
          ? window.app.platform.inspectionService.listInspections(this.buildCtx(), this.selection.equipmentId as any)
          : Promise.resolve([])),
        apply: (value, options) => { this.selection.inspectionId = value; this.inspections = [...options]; }
      }
    ]);
  }

  protected async loadWorkOrdersForReport() {
    if (!this.report) return;
    this.workOrders = await this.ctxLoad.load("Ordens de Serviço", () => window.app.platform.adminService.listWorkOrdersForDocument(this.buildCtx(), this.report!.clientId, this.report!.projectId || undefined));
    await this.loadSubWorkOrdersForReport();
  }

  protected async loadSubWorkOrdersForReport() {
    this.subWorkOrders = [];
    if (!this.report?.osWorkOrderId) return;
    this.subWorkOrders = await this.ctxLoad.load("Sub-OS", () => window.app.platform.adminService.listSubWorkOrdersForDocument(this.buildCtx(), this.report!.osWorkOrderId!));
  }

  protected async loadRegisteredSignatories() {
    if (!this.report) return;
    this.registeredSignatories = await window.app.platform.adminService.listSignatories(this.scopedCtx(this.report.clientId)).catch(() => []);
  }

  protected async loadSignatureImages() {
    if (!this.report) return;
    const ids = this.report.signatories.map(s => s.professionalId).filter((id): id is any => !!id);
    await Promise.all(ids.map(async id => {
      if (this.signatureImages.has(id)) return;
      try {
        const image = await window.app.platform.adminService.getProfessionalSignature(this.buildCtx(), id);
        this.signatureImages.set(id, isEmbeddableImage(image) ? image : null);
      } catch { this.signatureImages.set(id, null); }
    }));
  }

  protected async loadReview() {
    if (!this.report) return;
    if (this.config.plhReview !== false) {
      try {
        this.review = await window.app.platform.technicalReviewService.getDocumentReview(this.scopedCtx(this.report.clientId), this.config.documentType as TechnicalDocumentType, this.report.id);
      } catch { this.review = null; }
    }
    this.engineering = await loadNdtEngineeringState(this.config.documentType as TechnicalDocumentType, this.report);
  }

  /** Resumo do documento para o HOLD (Painel de Engenharia). */
  protected holdSummary() {
    const r: any = this.report;
    return { documentNumber: r.reportNumber, revision: r.revision, clientId: String(r.clientId), client: r.clientDisplayName || "", project: r.projectContract || "", unitArea: r.unitArea || "", tag: r.equipmentTag || "", osNumber: r.osNumber || "", subOsNumber: r.subOsNumber || "", inspectorProfessionalId: r.inspectorProfessionalId || null, inspectorName: r.inspectorName || r.createdBy || "", route: `${this.config.routeBase}/${r.id}` };
  }


  /** ENVIAR PARA O PLH: o modal valida o relatório pelo próprio serviço e lista só PLHs habilitados. */
  protected onSubmitToPlh() {
    const r = this.report;
    if (!r) return;
    openPlhSubmitModal({
      documentType: this.config.documentType as TechnicalDocumentType, documentId: r.id, clientId: String(r.clientId), documentNumber: r.reportNumber, revision: r.revision,
      client: r.clientDisplayName, project: r.projectContract, unitArea: r.unitArea, tag: r.equipmentTag, osNumber: r.osNumber, subOsNumber: r.subOsNumber,
      elaboratedBy: r.inspectorName || r.createdBy
    }, async review => {
      this.review = review;
      this.report = (await this.svc.getReport(this.scopedCtx(String(r.clientId)), r.id)) || this.report;
      this.saveNotice = `Relatório enviado para o PLH ${review.assignedPlhName}. Status: Aguardando Análise do PLH.`;
      this.noticeKind = "ok";
      this.render();
    });
  }

  /** Último certificado de calibração de um ativo de END (cadastro real) — ou nulo. */
  protected async latestCertificateOf(assetId: string): Promise<any | null> {
    try {
      const certs = await window.app.platform.ndtAssetService.listCertificates(this.scopedCtx(this.report?.clientId), assetId);
      return certs && certs.length > 0 ? certs[0] : null;
    } catch { return null; }
  }

  // --- Cliente → Projeto → Unidade/Área → Equipamento → Inspeção ---

  protected async loadClients() {
    this.clients = await this.ctxLoad.load("Clientes", () => window.app.platform.adminService.listAuthorizedClients(this.buildCtx()));
    const preselected = contextStore.get().activeClientId || "";
    if (preselected && this.clients.some((c: any) => c.id === preselected)) {
      this.selection.clientId = preselected;
      await this.loadProjects();
      await this.loadEquipments();
    }
  }

  protected async loadProjects() {
    this.projects = []; this.unitAreas = []; this.equipments = []; this.inspections = [];
    if (!this.selection.clientId) return;
    this.projects = await this.ctxLoad.load("Projetos", () => window.app.platform.projectService.listProjects(this.selection.clientId, undefined, this.buildCtx()));
  }

  protected async loadUnitAreas() {
    this.unitAreas = []; this.equipments = []; this.inspections = [];
    if (!this.selection.projectId) return;
    this.unitAreas = await this.ctxLoad.load("Unidades / Áreas", () => window.app.platform.projectService.listUnitAreas({ projectId: this.selection.projectId, clientId: this.selection.clientId }, undefined, this.buildCtx()));
  }

  protected async loadEquipments() {
    this.equipments = []; this.inspections = [];
    if (!this.selection.clientId) return;
    const all = await this.ctxLoad.load("Equipamentos", () => window.app.platform.equipmentService.listEquipments(this.scopedCtx(), { clientId: this.selection.clientId }));
    this.equipments = filterEquipmentsByContext(all || [], this.selection);
  }

  protected async loadInspections() {
    this.inspections = [];
    if (!this.selection.equipmentId) return;
    const list = await this.ctxLoad.load("Inspeções", () => window.app.platform.inspectionService.listInspections(this.scopedCtx(), this.selection.equipmentId as any));
    this.inspections = Array.from(list || []);
  }

  protected async resolveExistingReport() {
    this.existingReport = null;
    if (!this.selection.inspectionId) return;
    try {
      this.existingReport = await this.svc.getByInspection(this.scopedCtx(), this.selection.inspectionId);
    } catch (e: any) {
      this.contextError = this.messageOf(e, `Não foi possível verificar se já existe um ${this.config.reportLabel} para esta Inspeção.`);
    }
  }

  protected async onContextChange(field: keyof LpContextSelection, value: string) {
    this.contextError = null;
    this.selection[field] = value;
    if (field === "clientId") {
      this.selection.projectId = ""; this.selection.unitAreaId = ""; this.selection.equipmentId = ""; this.selection.inspectionId = "";
      await this.loadProjects(); await this.loadEquipments();
    } else if (field === "projectId") {
      this.selection.unitAreaId = ""; this.selection.equipmentId = ""; this.selection.inspectionId = "";
      await this.loadUnitAreas(); await this.loadEquipments();
    } else if (field === "unitAreaId") {
      this.selection.equipmentId = ""; this.selection.inspectionId = "";
      await this.loadEquipments();
    } else if (field === "equipmentId") {
      this.selection.inspectionId = "";
      await this.loadInspections();
    }
    await this.resolveExistingReport();
    this.render();
  }

  protected async onCreateFromContext() {
    if (this.isResolvingContext) return;
    const missing = missingLpContextFields(this.selection);
    if (missing.length > 0) {
      this.contextError = `Selecione ${missing.join(", ")} para criar o ${this.config.reportLabel}.`;
      this.render();
      return;
    }
    const stale = staleLpContextFields(this.selection, { clients: this.clients, projects: this.projects, unitAreas: this.unitAreas, equipments: this.equipments, inspections: this.inspections });
    if (stale.length > 0) {
      this.contextError = `Selecione novamente ${stale.join(", ")} — o contexto foi recarregado.`;
      this.render();
      return;
    }
    this.isResolvingContext = true;
    this.contextError = null;
    this.render();
    try {
      const inspection = this.inspections.find((i: any) => i.id === this.selection.inspectionId);
      const client = this.clients.find((c: any) => c.id === this.selection.clientId);
      const project = this.projects.find((p: any) => p.id === this.selection.projectId);
      const unitArea = this.unitAreas.find((u: any) => u.id === this.selection.unitAreaId);
      const equipment = this.equipments.find((e: any) => e.id === this.selection.equipmentId);
      const auth = contextStore.get();
      const loggedProf = this.professionals.find((p: any) => p.userId === auth.userId || p.id === auth.professionalId);
      const inspector = (inspection ? this.professionals.find((p: any) => p.id === inspection.inspectorId) : undefined) || loggedProf;
      const seed: NdtReportSeed = {
        clientId: this.selection.clientId,
        clientDisplayName: client ? (client.name || client.legalName || "") : "",
        projectId: this.selection.projectId || null,
        projectContract: project ? `${project.code} — ${project.name}` : "",
        unitAreaId: this.selection.unitAreaId || null,
        unitArea: unitArea ? `${unitArea.code} — ${unitArea.name}` : "",
        equipmentId: this.selection.equipmentId || null,
        equipmentTag: equipment?.tag || "",
        equipmentTypeLabel: equipment ? (EQUIPMENT_TYPE_LABELS[equipment.equipmentType] || equipment.equipmentType || "") : "",
        component: equipment?.name || "",
        baseMaterial: equipment ? (equipment.material || equipment.materialSpecification || equipment.bodyMaterial || "") : "",
        inspectionId: this.selection.inspectionId || null,
        inspectionNumber: formatInspectionReference(inspection),
        inspectorProfessionalId: inspection?.inspectorId || loggedProf?.id || null,
        inspectorName: inspector?.name || auth.userName || "",
        ...this.seedExtras(equipment)
      };
      const created = await this.svc.createDraft(this.scopedCtx(), seed);
      this.isResolvingContext = false;
      window.history.replaceState(null, "", `${this.config.routeBase}/${created.id}`);
      await this.openReport(created);
      this.applyEndDefaults();
    } catch (e: any) {
      console.error(`[${this.config.documentType}] Falha ao criar relatório`, e);
      this.isResolvingContext = false;
      await this.resolveExistingReport();
      this.contextError = this.existingReport
        ? `Esta Inspeção já possui o ${this.config.reportLabel} Nº ${this.existingReport.reportNumber}. Use "Abrir Relatório" para continuar o preenchimento.`
        : this.messageOf(e, `Erro ao criar o ${this.config.reportLabel}.`);
      this.render();
    }
  }

  protected async onOpenExisting() {
    if (!this.existingReport) return;
    window.history.replaceState(null, "", `${this.config.routeBase}/${this.existingReport.id}`);
    await this.openReport(this.existingReport);
  }

  protected async onChangeContext() {
    this.report = null; this.loadError = null; this.saveNotice = null; this.validationErrors = null; this.existingReport = null; this.contextError = null;
    this.selection = { clientId: "", projectId: "", unitAreaId: "", equipmentId: "", inspectionId: "" };
    this.projects = []; this.unitAreas = []; this.equipments = []; this.inspections = [];
    this.removeAttribute("id");
    window.history.replaceState(null, "", this.config.routeBase);
    this.isLoading = true;
    this.render();
    await this.loadClients();
    this.isLoading = false;
    this.render();
  }

  // ---------------------------------------------------------- MUTATIONS

  protected scheduleAutosave() {
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    // CONFIGURAÇÕES → GERAL: autosave desligado = só grava em "Salvar rascunho".
    const delay = autosaveDelayMs();
    if (delay === null) return;
    this.autosaveTimer = setTimeout(() => {
      this.persistNow().catch(e => { this.saveNotice = this.messageOf(e, "Não foi possível salvar o rascunho."); this.noticeKind = "error"; this.render(); });
    }, delay);
  }

  protected async persistNow(): Promise<void> {
    if (!this.report) return;
    this.isSaving = true;
    try {
      this.report = await this.svc.saveDraft(this.scopedCtx(this.report.clientId), this.report);
    } finally {
      this.isSaving = false;
    }
  }

  private handleFormInput(e: Event) {
    if (!this.report) return;
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    if (!target || !("dataset" in target)) return;
    const ds = target.dataset;
    if (this.handleSpecificInput(target, ds)) { this.scheduleAutosave(); return; }
    if (ds["photoKey"] && ds["photoField"]) {
      const photo = this.report.photos.find(p => p.key === ds["photoKey"]);
      if (photo) (photo as any)[ds["photoField"]!] = target.value;
      this.scheduleAutosave();
      return;
    }
    if (ds["sigRole"] && ds["sigField"]) {
      const s = this.ensureSignatory(ds["sigRole"] as NdtSignatory["role"]);
      (s as any)[ds["sigField"]!] = target.value;
      this.scheduleAutosave();
      return;
    }
    if (ds["field"] && ds["managed"] !== "true") {
      (this.report as any)[ds["field"]!] = target.value;
      if (ds["field"] === "testDate" || ds["field"] === "equipmentTag") this.refreshMetaStrip();
      this.scheduleAutosave();
    }
  }

  protected refreshMetaStrip() {
    if (!this.report) return;
    const tag = this.querySelector("#meta-tag");
    const date = this.querySelector("#meta-date");
    if (tag) tag.textContent = this.report.equipmentTag || "—";
    if (date) date.textContent = this.report.testDate ? formatCivilDateBR(this.report.testDate) : "—";
  }

  protected onProcedureChange(procedureId: string) {
    if (!this.report) return;
    const procedure = this.procedures.find(p => p.id === procedureId);
    this.report.procedureId = procedureId || null;
    if (!procedure) {
      this.report.procedureRevision = "";
      this.scheduleAutosave(); this.render();
      return;
    }
    this.report.procedureRevision = formatProcedureLabel(procedure);
    if (procedure.referenceStandard) {
      const match = this.matchCatalogItem(this.norms, procedure.referenceStandard);
      this.report.referenceNormId = match?.id ?? null;
      this.report.referenceNorm = match ? formatCatalogItemCode(match) : procedure.referenceStandard;
    }
    if (procedure.acceptanceCriteria) {
      const match = this.matchCatalogItem(this.criteria, procedure.acceptanceCriteria);
      this.report.acceptanceCriteriaId = match?.id ?? null;
      this.report.acceptanceCriteria = match ? formatCatalogItemLabel(match) : procedure.acceptanceCriteria;
    }
    this.scheduleAutosave(); this.render();
  }

  /**
   * CONFIGURAÇÕES → END: pré-seleciona procedimento, norma e critério PADRÃO
   * num relatório recém-criado. Só o que existe e está selecionável para o
   * método entra (um padrão inativado não é aplicado), e nada que a pessoa já
   * escolheu é trocado. O mesmo caminho da seleção manual — nenhum atalho.
   */
  protected applyEndDefaults() {
    const r = this.report;
    if (!r || r.procedureId || r.referenceNormId || r.acceptanceCriteriaId) return;
    const cfg = (operationalSettings().end as Record<string, EndMethodSettings | undefined>)[this.config.documentType];
    if (!cfg) return;
    if (cfg.defaultProcedureId && this.procedures.some(p => p.id === cfg.defaultProcedureId)) this.onProcedureChange(cfg.defaultProcedureId);
    if (cfg.defaultNormId && !this.report?.referenceNormId && this.norms.some(n => n.id === cfg.defaultNormId)) this.onCatalogSelection("referenceNorm", cfg.defaultNormId);
    if (cfg.defaultCriteriaId && !this.report?.acceptanceCriteriaId && this.criteria.some(c => c.id === cfg.defaultCriteriaId)) this.onCatalogSelection("acceptanceCriteria", cfg.defaultCriteriaId);
  }

  protected matchCatalogItem(items: NdtCatalogItem[], value: string): NdtCatalogItem | undefined {
    const target = value.trim().toLowerCase();
    if (!target) return undefined;
    return items.find(i => i.name.trim().toLowerCase() === target || (!!i.code && i.code.trim().toLowerCase() === target) || formatCatalogItemLabel(i).trim().toLowerCase() === target);
  }

  protected onCatalogSelection(field: "referenceNorm" | "acceptanceCriteria", itemId: string) {
    if (!this.report) return;
    const source = field === "referenceNorm" ? this.norms : this.criteria;
    const item = source.find(i => i.id === itemId);
    if (field === "referenceNorm") {
      this.report.referenceNormId = itemId || null;
      this.report.referenceNorm = item ? formatCatalogItemCode(item) : "";
    } else {
      this.report.acceptanceCriteriaId = itemId || null;
      this.report.acceptanceCriteria = item ? formatCatalogItemLabel(item) : "";
    }
    this.scheduleAutosave(); this.render();
  }

  protected async onWorkOrderChange(workOrderId: string) {
    if (!this.report) return;
    const wo = this.workOrders.find((w: any) => w.id === workOrderId);
    this.report.osWorkOrderId = workOrderId || null;
    this.report.osNumber = wo?.code || "";
    this.report.osSubWorkOrderId = null;
    this.report.subOsNumber = "";
    await this.loadSubWorkOrdersForReport();
    this.scheduleAutosave(); this.render();
  }

  protected onSubWorkOrderChange(subWorkOrderId: string) {
    if (!this.report) return;
    const sub = this.subWorkOrders.find((s: any) => s.id === subWorkOrderId);
    this.report.osSubWorkOrderId = subWorkOrderId || null;
    this.report.subOsNumber = sub?.code || "";
    this.scheduleAutosave(); this.render();
  }

  protected credentialFor(p: any): string {
    return p.crea || p.cft || p.snqc || p.sncc || p.endIso9712 || p.endIso || "";
  }

  protected ensureSignatory(role: NdtSignatory["role"]): NdtSignatory {
    if (!this.report) throw new Error("no report");
    let s = this.report.signatories.find(sg => sg.role === role);
    if (!s) {
      s = { role, professionalId: null, name: "", functionTitle: "", credential: "", signatureImageId: null, signedAt: null };
      this.report.signatories.push(s);
    }
    return s;
  }

  protected async onSignatoryProfessionalChange(role: NdtSignatory["role"], professionalId: string) {
    if (!this.report) return;
    const prof = this.professionals.find(p => p.id === professionalId);
    this.report.signatories = this.report.signatories.filter(s => s.role !== role);
    if (prof) {
      this.report.signatories.push({ role, professionalId: prof.id, name: prof.name || "", functionTitle: prof.functionTitle || NDT_SIGNATORY_ROLE_LABELS[role], credential: this.credentialFor(prof), signatureImageId: prof.signatureImageId || null, signedAt: null });
      if (role === "INSPETOR") {
        this.report.inspectorProfessionalId = prof.id;
        this.report.inspectorName = prof.name || "";
      }
    }
    await this.loadSignatureImages();
    this.scheduleAutosave(); this.render();
  }

  protected readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  protected rejectFile(file: File): string | null {
    if (!file.type.startsWith("image/")) return `"${file.name}" não é uma imagem. Anexe PNG, JPG ou WEBP.`;
    // Tamanho e extensões de CONFIGURAÇÕES → ARQUIVOS (padrão: 4 MB e PNG/JPG/JPEG/WEBP, como antes).
    return imageRejection(file);
  }

  protected async onCroquiFile(file: File) {
    if (!this.report) return;
    const rejected = this.rejectFile(file);
    if (rejected) { this.notify(rejected, "error"); return; }
    const image = await this.readFileAsDataUrl(file);
    if (!isEmbeddableImage(image)) { this.notify("Formato de imagem não suportado para o documento.", "error"); return; }
    this.report.croqui = { image, fileName: file.name, fileSize: file.size, uploadedAt: new Date().toISOString() };
    this.scheduleAutosave(); this.render();
  }

  protected async onPhotoFiles(files: FileList | File[]) {
    if (!this.report) return;
    const all = Array.from(files);
    // CONFIGURAÇÕES → ARQUIVOS: limite de fotos por relatório. Vale para o
    // anexo NOVO; um relatório que já tinha mais fotos não perde nenhuma.
    const slots = remainingPhotoSlots(this.report.photos.length);
    if (all.length > slots) {
      this.notify(slots === 0 ? `Limite de ${operationalSettings().files.maxPhotosPerReport} fotos por relatório atingido (Configurações → Arquivos).` : `Só cabem mais ${slots} foto(s) neste relatório (limite de ${operationalSettings().files.maxPhotosPerReport}, Configurações → Arquivos). As demais não foram anexadas.`, "error");
    }
    for (const file of all.slice(0, Number.isFinite(slots) ? slots : all.length)) {
      const rejected = this.rejectFile(file);
      if (rejected) { this.notify(rejected, "error"); continue; }
      const image = await this.readFileAsDataUrl(file);
      if (!isEmbeddableImage(image)) { this.notify(`"${file.name}": formato não suportado.`, "error"); continue; }
      this.report.photos.push({ key: `${this.config.documentType}-PHOTO-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, image, fileName: file.name, fileSize: file.size, uploadedAt: new Date().toISOString(), item: "", location: "", comment: "" });
    }
    this.scheduleAutosave(); this.render();
  }

  protected removePhoto(key: string) {
    if (!this.report) return;
    this.report.photos = this.report.photos.filter(p => p.key !== key);
    this.scheduleAutosave(); this.render();
  }

  protected notify(message: string, kind: "info" | "ok" | "error" = "info") {
    this.saveNotice = message;
    this.noticeKind = kind;
    this.render();
  }

  protected messageOf(e: any, fallback: string): string {
    return e instanceof AppError ? ((e as any).safeMessage || e.message) : (e?.message || fallback);
  }

  // ------------------------------------------------------------- ACTIONS

  protected async onSaveDraft() {
    if (!this.report) return;
    try {
      await this.persistNow();
      this.saveNotice = "Rascunho salvo com sucesso.";
      this.noticeKind = "ok";
      this.validationErrors = null;
      this.validationOk = false;
    } catch (e: any) {
      this.saveNotice = this.messageOf(e, "Erro ao salvar rascunho.");
      this.noticeKind = "error";
    }
    this.render();
  }

  protected async onValidate() {
    if (!this.report) return;
    try {
      await this.persistNow();
      const result = await this.svc.validate(this.scopedCtx(this.report.clientId), this.report.id);
      this.report = (await this.svc.getReport(this.scopedCtx(this.report.clientId), this.report.id)) || this.report;
      this.validationOk = result.valid;
      this.validationErrors = result.valid ? [] : result.errors;
      this.saveNotice = null;
    } catch (e: any) {
      this.validationErrors = [this.messageOf(e, "Erro ao validar dados.")];
      this.validationOk = false;
    }
    this.render();
  }

  /** "Visualizar Relatório" e "Gerar PDF": o MESMO snapshot — o Preview é o PDF renderizado. */
  protected async onPreviewOrPdf(generate: boolean) {
    if (!this.report) return;
    try {
      if (this.report.status !== "SIGNED") await this.persistNow();
      const ctx = this.scopedCtx(this.report.clientId);
      this.previewSnapshot = await this.svc.buildDocumentSnapshot(ctx, this.report.id);
      if (generate) {
        await this.svc.recordPdfGenerated(ctx, this.report.id);
        this.report = (await this.svc.getReport(ctx, this.report.id)) || this.report;
      }
      this.previewOpen = true;
      this.render();
    } catch (e: any) {
      console.error(`[${this.config.documentType}] Falha ao montar o documento`, e);
      this.notify(this.messageOf(e, "Erro ao gerar o relatório."), "error");
    }
  }

  protected async onSignAsPlh() {
    if (!this.report) return;
    try {
      this.report = await this.svc.signAsPlh(this.scopedCtx(this.report.clientId), this.report.id);
      await this.loadSignatureImages();
      this.saveNotice = "Relatório assinado como PLH. O documento foi congelado nesta revisão.";
      this.noticeKind = "ok";
    } catch (e: any) {
      this.saveNotice = this.messageOf(e, "Erro ao assinar como PLH.");
      this.noticeKind = "error";
    }
    this.render();
  }

  // -------------------------------------------------------------- RENDER

  render() {
    const has = (p: string) => contextStore.hasPermission(p);
    const cfg = this.config;
    if (this.isLoading) {
      this.innerHTML = `<style>${this.styles()}</style><div class="nr-loading"><span>Carregando ${this.esc(cfg.reportLabel)}...</span></div>`;
      return;
    }
    if (this.loadError) {
      this.innerHTML = `<style>${this.styles()}</style><div class="nr-empty"><h2>${this.esc(cfg.title.toUpperCase())}</h2><p class="err">${this.esc(this.loadError)}</p><div style="display:flex; gap:10px;"><button class="btn" id="btn-retry">Tentar novamente</button><button class="btn" id="btn-back">Voltar</button></div></div>`;
      this.querySelector("#btn-retry")?.addEventListener("click", () => { this.loadError = null; this.isLoading = true; this.init(); });
      this.querySelector("#btn-back")?.addEventListener("click", () => router.navigate(cfg.routeBase));
      return;
    }
    if (!this.report) { this.renderContextSelection(); return; }

    const r = this.report;
    const reviewLocked = reviewEditingLocked(this.review) || ndtHoldLocked(this.engineering);
    const canEdit = has(cfg.permissions.edit) && r.status !== "SIGNED" && !reviewLocked;
    const canValidate = has(cfg.permissions.validate) && r.status !== "SIGNED" && !reviewLocked;
    const canPdf = has(cfg.permissions.generatePdf);
    const canSign = has(cfg.permissions.approve) && has(cfg.permissions.sign) && r.status !== "SIGNED" && !ndtHoldLocked(this.engineering);
    const ro = !canEdit;

    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="nr-header">
        <div><h1>${this.esc(cfg.title)}</h1><p>${this.esc(cfg.subtitle)}</p></div>
        <div class="nr-actions">
          ${canEdit ? `<button class="btn" id="btn-save-draft" ${this.isSaving ? "disabled" : ""}><i data-lucide="save"></i> Salvar Rascunho</button>` : ""}
          ${canValidate ? `<button class="btn" id="btn-validate"><i data-lucide="check-circle"></i> Validar Dados</button>` : ""}
          <button class="btn" id="btn-preview"><i data-lucide="eye"></i> Visualizar Relatório</button>
          ${canPdf ? `<button class="btn primary" id="btn-generate-pdf"><i data-lucide="file-down"></i> Gerar PDF</button>` : ""}
          ${cfg.plhReview === false ? "" : plhSubmitButton(this.review, r.status)}
          ${ndtHoldButton(this.engineering, has(cfg.permissions.edit) && r.status !== "SIGNED" && !reviewEditingLocked(this.review))}
          <button class="btn" id="btn-history"><i data-lucide="history"></i> Histórico</button>
        </div>
      </div>
      <div class="nr-meta">
        <div class="meta"><span class="lbl">Nº do Relatório</span><span class="val">${this.esc(r.reportNumber)}</span></div>
        <div class="meta"><span class="lbl">Revisão</span><span class="val">${this.esc(r.revision)}</span></div>
        <div class="meta"><span class="lbl">Status</span><span class="status status-${r.status.toLowerCase()}">${this.engineering.hold ? "Em HOLD" : this.statusLabel(r.status)}</span></div>
        <div class="meta"><span class="lbl">Equipamento / TAG</span><span class="val" id="meta-tag">${this.esc(r.equipmentTag || "—")}</span></div>
        <div class="meta"><span class="lbl">Data do Ensaio</span><span class="val" id="meta-date">${r.testDate ? this.esc(formatCivilDateBR(r.testDate)) : "—"}</span></div>
      </div>
      ${this.renderContextStrip(r)}
      <!-- Card ANÁLISE DO PLH: mesmo componente do ME, mesmo serviço central. -->
      ${this.review ? plhApprovalPanel(this.review) : ""}
      ${aiReviewSlot(this.config.documentType as "PM" | "USC" | "BSCAN", r.id, `Relatório ${this.config.documentType} ${r.reportNumber || ""}`)}
      ${cfg.plhReview === false ? "" : reviewBanner(this.review)}
      ${ndtEngineeringBanners(this.engineering, canResolveCorrections())}
      ${this.saveNotice ? `<div class="nr-notice nr-notice-${this.noticeKind}">${this.esc(this.saveNotice)}</div>` : ""}
      ${this.validationErrors !== null ? this.renderValidationBanner() : ""}
      <div class="nr-layout">
        <nav class="nr-nav">${cfg.sections.map(([num, label]) => `<a class="nr-nav-item ${this.activeSection === num ? "active" : ""}" data-sec="${num}" href="#nr-sec-${num}"><span class="num">${num}</span><span>${label}</span></a>`).join("")}</nav>
        <div class="nr-main">${this.renderCards(r, ro, canSign)}</div>
      </div>
      ${this.previewOpen ? this.renderPreviewModal(r) : ""}
      ${this.historyOpen ? this.renderHistoryModal(r) : ""}
      ${this.lightbox.open ? this.renderLightbox() : ""}
    `;
    createIcons({ icons: { Save, Eye, CheckCircle, FileDown, Plus, Trash2, Upload, PenLine, History, RefreshCw, Send, Image: ImageIcon }, nameAttr: "data-lucide", root: this });
    if (this.previewOpen && this.previewSnapshot) {
      const preview = this.querySelector("#nr-pdf-preview") as any;
      if (preview) preview.snapshot = this.previewSnapshot;
    }
    this.attachEvents(ro);
    bindNdtEngineeringEvents(this, {
      documentType: cfg.documentType as TechnicalDocumentType, report: r, state: this.engineering,
      currentStatus: () => ({ status: r.status, label: this.statusLabel(r.status) }),
      summary: () => this.holdSummary(),
      onChanged: async message => { await this.loadReview(); this.notify(message, "ok"); }
    });
    ndtDeepLinkFocus(this, "nr", cfg.sections);
  }

  protected statusLabel(status: string): string {
    return status === "SIGNED" ? "Assinado" : status === "VALIDATED" ? "Validado" : "Em Elaboração";
  }

  protected laudoClass(laudo: string): string {
    return laudo === "APROVADO" ? "ok" : laudo === "REPROVADO" ? "bad" : laudo === "EXAME_COMPLEMENTAR" ? "warn" : laudo === "EM_ELABORACAO" ? "neutral" : "none";
  }

  protected opt(value: string, label: string, selected: boolean) {
    return `<option value="${this.esc(value)}" ${selected ? "selected" : ""}>${this.esc(label)}</option>`;
  }

  protected dis(ro: boolean) { return ro ? "disabled" : ""; }

  protected renderContextStrip(r: R): string {
    return `
      <section class="nr-card nr-context">
        <div class="card-head"><span class="card-title">Contexto do Relatório</span><button class="btn sm" id="btn-change-context" title="Selecionar outro contexto documental">Trocar Contexto</button></div>
        <div class="grid cols-5">
          <label class="field"><span>Cliente *</span><input class="ctl" readonly value="${this.esc(r.clientDisplayName || "—")}"></label>
          <label class="field"><span>Projeto *</span><input class="ctl" readonly value="${this.esc(r.projectContract || "—")}"></label>
          <label class="field"><span>Unidade / Área *</span><input class="ctl" readonly value="${this.esc(r.unitArea || "—")}"></label>
          <label class="field"><span>Equipamento / TAG *</span><input class="ctl" readonly value="${this.esc(r.equipmentTag ? `${r.equipmentTag}${r.component ? " — " + r.component : ""}` : "—")}"></label>
          <label class="field"><span>Inspeção</span><input class="ctl" readonly value="${this.esc(r.inspectionNumber || "—")}"></label>
        </div>
      </section>`;
  }

  protected renderContextSelection() {
    const sel = this.selection;
    const ready = missingLpContextFields(sel).length === 0;
    const canCreate = contextStore.hasPermission(this.config.permissions.create);
    const inspectionChosen = !!sel.inspectionId;
    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="nr-header"><div><h1>${this.esc(this.config.title)}</h1><p>${this.esc(this.config.subtitle)}</p></div></div>
      <section class="nr-card nr-context">
        <div class="card-head"><span class="card-title">Contexto do Relatório</span></div>
        <p class="hint" style="margin:0 0 12px 0;">Selecione Cliente → Projeto → Unidade/Área → Equipamento. A Inspeção é opcional. Os cadastros reais alimentam o relatório; o cliente ativo da sessão é apenas uma pré-seleção.</p>
        ${this.ctxLoad.banner()}${hydrationBanner(this.hydration)}
        <div class="grid cols-5">
          <label class="field"><span>Cliente *</span><select class="ctl" id="sel-ctx-client">${this.opt("", this.ctxLoad.emptyLabel("Clientes", "Nenhum Cliente ativo cadastrado", "Selecione o Cliente..."), !sel.clientId)}${this.clients.map((c: any) => this.opt(c.id, c.name || c.legalName, sel.clientId === c.id)).join("")}</select></label>
          <label class="field"><span>Projeto *</span><select class="ctl" id="sel-ctx-project" ${!sel.clientId ? "disabled" : ""}>${this.opt("", !sel.clientId ? "Selecione o Cliente primeiro" : this.projects.length === 0 ? "Nenhum Projeto para este Cliente" : "Selecione o Projeto...", !sel.projectId)}${this.projects.map((p: any) => this.opt(p.id, `${p.code} — ${p.name}`, sel.projectId === p.id)).join("")}</select></label>
          <label class="field"><span>Unidade / Área *</span><select class="ctl" id="sel-ctx-unit-area" ${!sel.projectId ? "disabled" : ""}>${this.opt("", !sel.projectId ? "Selecione o Projeto primeiro" : this.unitAreas.length === 0 ? "Nenhuma Unidade/Área neste Projeto" : "Selecione a Unidade/Área...", !sel.unitAreaId)}${this.unitAreas.map((u: any) => this.opt(u.id, `${u.code} — ${u.name}`, sel.unitAreaId === u.id)).join("")}</select></label>
          <label class="field"><span>Equipamento / TAG *</span><select class="ctl" id="sel-ctx-equipment" ${!sel.clientId ? "disabled" : ""}>${this.opt("", !sel.clientId ? "Selecione o Cliente primeiro" : this.ctxLoad.emptyLabel("Equipamentos", "Nenhum Equipamento neste contexto", "Selecione o Equipamento..."), !sel.equipmentId)}${this.equipments.map((e: any) => this.opt(e.id, `${e.tag} — ${e.name || ""}`, sel.equipmentId === e.id)).join("")}</select></label>
          <label class="field"><span>Inspeção (opcional)</span><select class="ctl" id="sel-ctx-inspection" ${!sel.equipmentId ? "disabled" : ""}>${this.opt("", !sel.equipmentId ? "Selecione o Equipamento primeiro" : this.ctxLoad.emptyLabel("Inspeções", "Nenhuma inspeção para este equipamento — pode prosseguir sem", "Selecione uma inspeção, se aplicável"), !sel.inspectionId)}${this.inspections.map((i: any) => this.opt(i.id, `${formatInspectionReference(i)}${i.status ? " — " + i.status : ""}`, sel.inspectionId === i.id)).join("")}</select></label>
        </div>
        ${this.contextError ? `<div class="nr-notice nr-notice-error" style="margin-top:14px;">${this.esc(this.contextError)}</div>` : ""}
        ${inspectionChosen && this.existingReport ? `<div class="nr-notice" style="margin-top:14px;"><strong>${this.esc(this.config.reportLabel.toUpperCase())} ENCONTRADO</strong> — Nº ${this.esc(this.existingReport.reportNumber)} · Rev. ${this.esc(this.existingReport.revision)} · ${this.statusLabel(this.existingReport.status)}</div>` : ""}
        <div class="nr-actions" style="margin-top:16px; justify-content:flex-start;">
          ${inspectionChosen && this.existingReport
            ? `<button class="btn primary" id="btn-open-existing">Abrir Relatório</button>`
            : canCreate
              ? `<button class="btn primary" id="btn-create" ${this.isResolvingContext || !ready ? "disabled" : ""}>${this.isResolvingContext ? "Criando..." : `Criar ${this.esc(this.config.reportLabel)}`}</button>`
              : ``}
        </div>
      </section>`;
    this.querySelector("#sel-ctx-client")?.addEventListener("change", e => this.onContextChange("clientId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-project")?.addEventListener("change", e => this.onContextChange("projectId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-unit-area")?.addEventListener("change", e => this.onContextChange("unitAreaId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-equipment")?.addEventListener("change", e => this.onContextChange("equipmentId", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-ctx-inspection")?.addEventListener("change", e => this.onContextChange("inspectionId", (e.target as HTMLSelectElement).value));
    this.querySelector("#btn-create")?.addEventListener("click", () => this.onCreateFromContext());
    this.querySelector("#btn-open-existing")?.addEventListener("click", () => this.onOpenExisting());
  }

  protected renderValidationBanner(): string {
    if (this.validationOk) return `<div class="nr-notice nr-notice-ok">Dados validados — o relatório está completo.</div>`;
    return `<div class="nr-notice nr-notice-error"><strong>Pendências encontradas:</strong><ul>${(this.validationErrors || []).map(e => `<li>${this.esc(e)}</li>`).join("")}</ul></div>`;
  }

  // ------------------------------------------------------ COMMON CARDS

  /** Procedimento / Norma / Critério / Data / Inspetor / OS / Sub-OS / Rel. cliente — campos do item 01/02 comuns. */
  protected renderProcedureFields(r: R, ro: boolean): string {
    const inspectors = this.professionals.filter((p: any) => p.status === "ACTIVE");
    return `
      <div class="grid cols-4">
        <label class="field"><span>Procedimento ${this.config.documentType === "USC" ? "US" : this.config.documentType} *</span>
          <select class="ctl" id="sel-procedure" ${this.dis(ro)}>${this.opt("", this.procedures.length === 0 ? "Nenhum procedimento ativo para este método" : "Selecione...", !r.procedureId)}${this.procedures.map(p => this.opt(p.id, formatProcedureLabel(p), r.procedureId === p.id)).join("")}</select></label>
        <label class="field"><span>Norma de Referência *</span>
          <select class="ctl" id="sel-norm" ${this.dis(ro)}>${this.opt("", this.norms.length === 0 ? "Nenhuma norma cadastrada para o método" : "Selecione...", !r.referenceNormId)}${this.norms.map(n => this.opt(n.id, formatCatalogItemLabel(n), r.referenceNormId === n.id)).join("")}</select>
          ${!r.referenceNormId && r.referenceNorm ? `<small class="hint">Valor do procedimento: ${this.esc(r.referenceNorm)}</small>` : ""}</label>
        <label class="field"><span>Critério de Aceitação *</span>
          <select class="ctl" id="sel-criteria" ${this.dis(ro)}>${this.opt("", this.criteria.length === 0 ? "Nenhum critério cadastrado para o método" : "Selecione...", !r.acceptanceCriteriaId)}${this.criteria.map(c => this.opt(c.id, formatCatalogItemLabel(c), r.acceptanceCriteriaId === c.id)).join("")}</select>
          ${!r.acceptanceCriteriaId && r.acceptanceCriteria ? `<small class="hint">Valor do procedimento: ${this.esc(r.acceptanceCriteria)}</small>` : ""}</label>
        <label class="field"><span>Data do Ensaio *</span><input class="ctl" type="date" data-field="testDate" value="${this.esc(r.testDate)}" ${this.dis(ro)}></label>
      </div>
      <div class="grid cols-4" style="margin-top:12px;">
        <label class="field"><span>Inspetor Responsável *</span>
          ${(() => {
            const auth = contextStore.get();
            const canManage = this.canManageSignatories(r);
            const prof = this.professionals.find((p: any) => p.id === r.inspectorProfessionalId || p.userId === auth.userId || p.id === auth.professionalId);
            const inspectorDisplayName = r.inspectorName || prof?.name || auth.userName || "Inspetor Responsável";

            if (canManage) {
              return `
                <select class="ctl" id="sel-inspector" ${this.dis(ro)}>${this.opt("", "Selecione...", !r.inspectorProfessionalId)}${inspectors.map((p: any) => this.opt(p.id, `${p.name}${p.functionTitle ? " — " + p.functionTitle : ""}`, r.inspectorProfessionalId === p.id)).join("")}</select>
                <small class="hint">Definido pela Engenharia (Signatários).</small>`;
            }
            return `
              <input class="ctl" value="${this.esc(inspectorDisplayName)}" readonly style="background: var(--color-surface-sunken, #f1f5f9); cursor: not-allowed;" title="Resolvido automaticamente pelo usuário autenticado.">
              <small class="hint">Resolvido pela sua conta logada.</small>`;
          })()}</label>
        <label class="field"><span>Ordem de Serviço</span>
          <select class="ctl" id="sel-work-order" ${this.dis(ro)}>${this.opt("", this.ctxLoad.emptyLabel("Ordens de Serviço", "Nenhuma OS cadastrada para este Cliente", "Selecione..."), !r.osWorkOrderId)}${this.workOrders.map((w: any) => this.opt(w.id, `${w.code}${w.description ? " — " + w.description : ""}`, r.osWorkOrderId === w.id)).join("")}</select></label>
        <label class="field"><span>Sub-OS</span>
          <select class="ctl" id="sel-sub-work-order" ${this.dis(ro || !r.osWorkOrderId)}>${this.opt("", !r.osWorkOrderId ? "Selecione a OS primeiro" : this.ctxLoad.emptyLabel("Sub-OS", "Nenhuma Sub-OS para esta OS", "Selecione..."), !r.osSubWorkOrderId)}${this.subWorkOrders.map((s: any) => this.opt(s.id, `${s.code}${s.description ? " — " + s.description : ""}`, r.osSubWorkOrderId === s.id)).join("")}</select></label>
        <label class="field"><span>Rel. Cliente nº</span><input class="ctl" data-field="clientReportNumber" value="${this.esc(r.clientReportNumber)}" placeholder="Referência interna do cliente" ${this.dis(ro)}></label>
      </div>`;
  }

  protected renderLaudoSelect(key: string, keyAttr: string, fieldAttr: string, laudo: NdtLaudo, ro: boolean): string {
    return `<span class="laudo-badge ${this.laudoClass(laudo)}" data-laudo-badge="${key}"><select class="laudo-select" data-${keyAttr}="${key}" data-${fieldAttr}="laudo" ${this.dis(ro)}>${NDT_LAUDO_OPTIONS.map(o => this.opt(o.value, o.label, laudo === o.value)).join("")}</select></span>`;
  }

  protected renderCardCroqui(r: R, ro: boolean, num: string): string {
    const c = r.croqui;
    return `
      <section class="nr-card" id="nr-sec-${num}">
        <div class="card-head"><span class="card-title"><span class="card-num">${num}.</span> Croqui</span>${!ro && !c ? `<button class="btn sm primary" id="btn-croqui-attach"><i data-lucide="upload"></i> Anexar Croqui</button>` : ""}</div>
        ${c ? `
          <div class="attachment">
            <img class="thumb" src="${c.image}" alt="Croqui" data-lightbox="${this.esc(c.fileName)}">
            <div class="attachment-info">
              <div class="file-name">${this.esc(c.fileName)}</div>
              <div class="file-meta">${this.esc(formatFileSize(c.fileSize))}${c.uploadedAt ? " · " + this.esc(formatCivilDateBR(c.uploadedAt.slice(0, 10))) : ""}</div>
              <div class="attachment-actions">
                <button class="btn sm" data-view-image="croqui"><i data-lucide="eye"></i> Visualizar</button>
                ${!ro ? `<button class="btn sm" id="btn-croqui-replace"><i data-lucide="refresh-cw"></i> Substituir</button><button class="btn sm danger" id="btn-croqui-remove"><i data-lucide="trash-2"></i> Remover</button>` : ""}
              </div>
            </div>
          </div>` : `<label class="dropzone" for="inp-croqui"><i data-lucide="image"></i><span>Arraste o croqui aqui ou clique para selecionar</span><small>PNG, JPG ou WEBP · até ${formatFileSize(operationalSettings().files.maxImageMb * 1024 * 1024)}</small></label>`}
        <input type="file" id="inp-croqui" accept="image/png,image/jpeg,image/webp" style="display:none;" ${this.dis(ro)}>
      </section>`;
  }

  protected renderCardPhotos(r: R, ro: boolean, num: string): string {
    return `
      <section class="nr-card" id="nr-sec-${num}">
        <div class="card-head"><span class="card-title"><span class="card-num">${num}.</span> Evidências Fotográficas <span class="count">${r.photos.length}</span></span>${!ro ? `<button class="btn sm primary" id="btn-photo-add"><i data-lucide="plus"></i> Adicionar Foto</button>` : ""}</div>
        ${r.photos.length === 0 ? `<label class="dropzone" for="inp-photos"><i data-lucide="image"></i><span>Arraste as fotografias aqui ou clique para selecionar</span><small>${operationalSettings().files.maxPhotosPerReport === null ? "Quantidade livre" : `Até ${operationalSettings().files.maxPhotosPerReport} fotos`} · ${operationalSettings().files.allowedImageExtensions.map(e => e.toUpperCase()).join(", ")} · até ${formatFileSize(operationalSettings().files.maxImageMb * 1024 * 1024)} cada</small></label>` : `
          <div class="photo-grid">${r.photos.map((p, i) => `
            <div class="photo-card">
              <img class="thumb" src="${p.image}" alt="${this.esc(p.fileName)}" data-lightbox="${this.esc(p.fileName)}">
              <div class="photo-body">
                <div class="file-name">Foto ${String(i + 1).padStart(2, "0")} · ${this.esc(p.fileName)}</div>
                <div class="file-meta">${this.esc(formatFileSize(p.fileSize))}${p.uploadedAt ? " · " + this.esc(formatCivilDateBR(p.uploadedAt.slice(0, 10))) : ""}</div>
                <input class="ctl sm" data-photo-key="${p.key}" data-photo-field="item" value="${this.esc(p.item)}" placeholder="Componente (ex.: J-01)" ${this.dis(ro)}>
                <input class="ctl sm" data-photo-key="${p.key}" data-photo-field="location" value="${this.esc(p.location)}" placeholder="Localização (ex.: Vista Geral)" ${this.dis(ro)}>
                <input class="ctl sm" data-photo-key="${p.key}" data-photo-field="comment" value="${this.esc(p.comment)}" placeholder="Comentário" ${this.dis(ro)}>
                <div class="attachment-actions"><button class="btn sm" data-view-photo="${p.key}"><i data-lucide="eye"></i></button>${!ro ? `<button class="btn sm danger" data-remove-photo="${p.key}"><i data-lucide="trash-2"></i></button>` : ""}</div>
              </div>
            </div>`).join("")}</div>`}
        <input type="file" id="inp-photos" accept="image/png,image/jpeg,image/webp" multiple style="display:none;" ${this.dis(ro)}>
      </section>`;
  }

  protected renderCardObservations(r: R, ro: boolean, num: string): string {
    return `
      <section class="nr-card" id="nr-sec-${num}">
        <div class="card-head"><span class="card-title"><span class="card-num">${num}.</span> Observações e Parecer Final</span></div>
        <textarea class="ctl textarea" data-field="observations" placeholder="Registre aqui as observações e o parecer final do ensaio. O sistema não gera conclusão — somente o texto salvo aqui vai para o relatório." ${this.dis(ro)}>${this.esc(r.observations)}</textarea>
      </section>`;
  }

  protected canManageSignatories(r: R): boolean {
    return canManageLpSignatories(contextStore.hasPermission(this.config.permissions.manageSignatories), r.status);
  }

  protected renderSignatoryOptions(role: NdtSignatory["role"], r: R, currentId: string | null): string {
    if (role === "PLH") {
      const candidates = plhCandidatesForClient(this.professionals, r.clientId);
      return this.opt("", candidates.length === 0 ? "Nenhum PLH habilitado para este Cliente" : "Selecione o Engenheiro / PLH...", !currentId) + candidates.map((p: any) => this.opt(p.id, formatSignatoryOption(p), currentId === p.id)).join("");
    }
    const groups = role === "INSPETOR" ? inspectorCandidates(this.professionals, currentId) : fiscalCandidates(this.professionals, this.registeredSignatories, currentId);
    const preferredLabel = role === "INSPETOR" ? "Inspetores" : "Signatários cadastrados do Cliente";
    const group = (label: string, list: any[]) => list.length === 0 ? "" : `<optgroup label="${this.esc(label)}">${list.map((p: any) => this.opt(p.id, formatSignatoryOption(p), currentId === p.id)).join("")}</optgroup>`;
    return this.opt("", "Selecione...", !currentId) + group(preferredLabel, groups.preferred) + group("Outros profissionais ativos", groups.others);
  }

  protected renderSignatoryCard(r: R, role: NdtSignatory["role"], ro: boolean, canSign: boolean): string {
    const s = r.signatories.find(x => x.role === role);
    const image = s?.professionalId ? this.signatureImages.get(s.professionalId) : null;
    const signed = role === "PLH" && r.status === "SIGNED";
    const canChoose = !ro && !signed && this.canManageSignatories(r);
    return `
      <div class="sig-card ${canChoose ? "" : "readonly"}">
        <div class="sig-role">${NDT_SIGNATORY_ROLE_LABELS[role]}</div>
        <div class="sig-image">${image ? `<img src="${image}" alt="Assinatura">` : `<span class="sig-blank">${s?.professionalId ? "sem assinatura cadastrada" : "—"}</span>`}</div>
        ${canChoose ? `<select class="ctl sm" data-signatory-role="${role}">${this.renderSignatoryOptions(role, r, s?.professionalId ?? null)}</select>` : `<div class="sig-locked">${s?.professionalId ? "definido pela Engenharia" : "a definir pela Engenharia"}</div>`}
        <div class="sig-name">${this.esc(s?.name || "—")}</div>
        <div class="sig-meta">${this.esc(s?.functionTitle || "")}</div>
        <div class="sig-meta">${this.esc(s?.credential || (s ? "sem credencial cadastrada" : ""))}</div>
        ${role === "PLH" ? `<div class="sig-foot">${signed ? `<span class="status status-signed">Assinado em ${this.esc(formatCivilDateBR((r.signedAt || "").slice(0, 10)))}</span>` : canSign ? `<button class="btn sm primary" id="btn-sign-plh"><i data-lucide="pen-line"></i> Assinar como PLH</button>` : `<small class="hint">Só o PLH habilitado pelo gate assina.</small>`}</div>` : ""}
      </div>`;
  }

  protected renderCardSignatories(r: R, ro: boolean, canSign: boolean, num: string): string {
    return `
      <section class="nr-card" id="nr-sec-${num}">
        <div class="card-head"><span class="card-title"><span class="card-num">${num}.</span> Termo de Responsabilidade & Signatários</span></div>
        <div class="sig-grid">${this.renderSignatoryCard(r, "INSPETOR", ro, canSign)}${this.renderSignatoryCard(r, "PLH", ro, canSign)}${this.renderSignatoryCard(r, "FISCAL", ro, canSign)}</div>
        <p class="hint" style="margin-top:10px;">${this.canManageSignatories(r) ? "Você escolhe os três signatários. Escolher o PLH não o torna PLH: só o Profissional habilitado pelo gate assina." : "Os signatários são definidos pelo Engenheiro autorizado. Os cards estão em modo leitura."} As assinaturas são as imagens reais cadastradas em cada Profissional.</p>
      </section>`;
  }

  protected renderPreviewModal(r: R): string {
    return `<div class="modal-backdrop" id="nr-preview-modal"><div class="modal-box preview"><div class="modal-head"><span><i data-lucide="file-down"></i> ${this.esc(this.config.reportLabel)} — ${this.esc(r.reportNumber)} · Rev. ${this.esc(r.revision)}</span><button class="icon-btn" id="btn-close-preview">✕</button></div><div style="flex:1; position:relative; overflow:hidden;"><app-pdf-preview id="nr-pdf-preview" style="height:100%; display:block;"></app-pdf-preview></div></div></div>`;
  }

  protected renderHistoryModal(r: R): string {
    const entries = [...(r.history || [])].reverse();
    const labels: Record<string, string> = { CREATED: "Criação", SAVED: "Rascunho salvo", VALIDATED: "Validação", SIGNED: "Assinatura PLH", PDF_GENERATED: "PDF gerado" };
    return `<div class="modal-backdrop" id="nr-history-modal"><div class="modal-box"><div class="modal-head"><span><i data-lucide="history"></i> Histórico — ${this.esc(r.reportNumber)}</span><button class="icon-btn" id="btn-close-history">✕</button></div><div class="modal-body">${entries.length === 0 ? `<p class="hint">Sem eventos registrados.</p>` : `<table class="nr-table"><thead><tr><th>Quando</th><th>Ação</th><th>Detalhe</th><th>Por</th></tr></thead><tbody>${entries.map(h => `<tr><td>${this.esc(new Date(h.at).toLocaleString("pt-BR"))}</td><td>${this.esc(labels[h.action] || h.action)}</td><td>${this.esc(h.detail)}</td><td>${this.esc(h.by)}</td></tr>`).join("")}</tbody></table>`}</div></div></div>`;
  }

  protected renderLightbox(): string {
    return `<div class="modal-backdrop" id="nr-lightbox"><div class="modal-box lightbox"><div class="modal-head"><span>${this.esc(this.lightbox.title)}</span><button class="icon-btn" id="btn-close-lightbox">✕</button></div><div class="modal-body" style="display:flex; align-items:center; justify-content:center;"><img src="${this.lightbox.src}" alt="${this.esc(this.lightbox.title)}" style="max-width:100%; max-height:78vh; object-fit:contain;"></div></div></div>`;
  }

  // ------------------------------------------------------------- EVENTS

  protected attachEvents(ro: boolean) {
    this.querySelector("#btn-save-draft")?.addEventListener("click", () => this.onSaveDraft());
    this.querySelector("#btn-validate")?.addEventListener("click", () => this.onValidate());
    this.querySelector("#btn-preview")?.addEventListener("click", () => this.onPreviewOrPdf(false));
    this.querySelector("#btn-generate-pdf")?.addEventListener("click", () => this.onPreviewOrPdf(true));
    this.querySelector("#btn-history")?.addEventListener("click", () => { this.historyOpen = true; this.render(); });
    this.querySelector("#btn-close-history")?.addEventListener("click", () => { this.historyOpen = false; this.render(); });
    this.querySelector("#btn-close-preview")?.addEventListener("click", () => { this.previewOpen = false; this.render(); });
    this.querySelector("#btn-close-lightbox")?.addEventListener("click", () => { this.lightbox = { open: false, src: "", title: "" }; this.render(); });
    this.querySelector("#btn-sign-plh")?.addEventListener("click", () => this.onSignAsPlh());
    this.querySelector("#btn-submit-to-plh")?.addEventListener("click", () => this.onSubmitToPlh());
    // Card ANÁLISE DO PLH: iniciar análise, devolver e aprovar — pelo MESMO
    // serviço central do ME. Antes estes botões não existiam nesta tela.
    bindPlhApprovalPanel(this, {
      review: this.review,
      clientId: String(this.report?.clientId ?? ""),
      context: (clientId: string) => this.scopedCtx(clientId),
      onChanged: async (review) => { this.review = review; await this.loadReview(); this.render(); },
      onFailure: (message) => { this.saveNotice = message; this.render(); }
    });
    this.querySelector("#btn-change-context")?.addEventListener("click", () => this.onChangeContext());
    this.querySelectorAll(".nr-nav-item").forEach(a => a.addEventListener("click", () => {
      const sec = (a as HTMLElement).dataset["sec"] || "01";
      this.activeSection = sec;
      this.querySelectorAll(".nr-nav-item").forEach(n => n.classList.toggle("active", (n as HTMLElement).dataset["sec"] === sec));
    }));
    this.querySelector("#sel-procedure")?.addEventListener("change", e => this.onProcedureChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-norm")?.addEventListener("change", e => this.onCatalogSelection("referenceNorm", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-criteria")?.addEventListener("change", e => this.onCatalogSelection("acceptanceCriteria", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-inspector")?.addEventListener("change", e => this.onSignatoryProfessionalChange("INSPETOR", (e.target as HTMLSelectElement).value));
    this.querySelector("#sel-work-order")?.addEventListener("change", e => this.onWorkOrderChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-sub-work-order")?.addEventListener("change", e => this.onSubWorkOrderChange((e.target as HTMLSelectElement).value));
    this.querySelectorAll("[data-signatory-role]").forEach(sel => sel.addEventListener("change", e => this.onSignatoryProfessionalChange((sel as HTMLElement).dataset["signatoryRole"] as NdtSignatory["role"], (e.target as HTMLSelectElement).value)));
    this.querySelectorAll("[data-lightbox]").forEach(img => img.addEventListener("click", () => { this.lightbox = { open: true, src: (img as HTMLImageElement).src, title: (img as HTMLElement).dataset["lightbox"] || "" }; this.render(); }));
    this.querySelector("[data-view-image='croqui']")?.addEventListener("click", () => { if (this.report?.croqui) { this.lightbox = { open: true, src: this.report.croqui.image, title: this.report.croqui.fileName }; this.render(); } });
    this.querySelectorAll("[data-view-photo]").forEach(btn => btn.addEventListener("click", () => { const photo = this.report?.photos.find(p => p.key === (btn as HTMLElement).dataset["viewPhoto"]); if (photo) { this.lightbox = { open: true, src: photo.image, title: photo.fileName }; this.render(); } }));
    this.attachSpecificEvents(ro);
    if (ro) return;
    const croquiInput = this.querySelector("#inp-croqui") as HTMLInputElement | null;
    this.querySelector("#btn-croqui-attach")?.addEventListener("click", () => croquiInput?.click());
    this.querySelector("#btn-croqui-replace")?.addEventListener("click", () => croquiInput?.click());
    this.querySelector("#btn-croqui-remove")?.addEventListener("click", () => { if (this.report) { this.report.croqui = null; this.scheduleAutosave(); this.render(); } });
    croquiInput?.addEventListener("change", () => { const f = croquiInput.files?.[0]; if (f) this.onCroquiFile(f); });
    this.bindDropzone("label[for='inp-croqui']", files => { const f = files[0]; if (f) this.onCroquiFile(f); });
    const photoInput = this.querySelector("#inp-photos") as HTMLInputElement | null;
    this.querySelector("#btn-photo-add")?.addEventListener("click", () => photoInput?.click());
    photoInput?.addEventListener("change", () => { if (photoInput.files && photoInput.files.length > 0) this.onPhotoFiles(photoInput.files); });
    this.querySelectorAll("[data-remove-photo]").forEach(btn => btn.addEventListener("click", () => this.removePhoto((btn as HTMLElement).dataset["removePhoto"]!)));
    this.bindDropzone("label[for='inp-photos']", files => this.onPhotoFiles(files));
  }

  protected bindDropzone(selector: string, onFiles: (files: File[]) => void) {
    const zone = this.querySelector(selector) as HTMLElement | null;
    if (!zone) return;
    zone.addEventListener("dragover", e => { e.preventDefault(); zone.classList.add("over"); });
    zone.addEventListener("dragleave", () => zone.classList.remove("over"));
    zone.addEventListener("drop", e => { e.preventDefault(); zone.classList.remove("over"); const files = Array.from(e.dataTransfer?.files || []); if (files.length > 0) onFiles(files); });
  }

  protected esc(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
  }

  // -------------------------------------------------------------- STYLES

  protected hydrationStyles(): string { return CONTEXT_HYDRATION_STYLES; }

  protected styles(): string {
    return `
      ${PLH_REVIEW_UI_STYLES}
      ${CONTEXT_LOAD_STYLES}
      ${CONTEXT_HYDRATION_STYLES}
      ${HOLD_UI_STYLES}
      ${CORRECTION_UI_STYLES}
      ${this.tagName.toLowerCase()} { display:block; --nr-blue:#2F6FD6; --nr-blue-soft:rgba(47,111,214,0.16); --nr-card:var(--ia-bg-panel, #161A22); --nr-card-border:var(--ia-line, #262C38); --nr-input:var(--ia-bg-panel, #0F131A); --nr-input-border:var(--ia-line, #2B3340); --nr-text:var(--ia-fg, #E6E9EF); --nr-muted:var(--ia-fg-muted, #8A94A6); --nr-label:var(--ia-fg-soft, #A7B0C0); }
      .nr-loading, .nr-empty { display:flex; flex-direction:column; align-items:center; gap:12px; padding:64px 24px; color: var(--nr-muted); text-align:center; }
      .nr-empty h2 { margin:0; font-size:1.05rem; color: var(--nr-text); } .nr-empty .err { color: var(--ia-danger-fg, #fca5a5); margin:0; }
      .nr-header { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; flex-wrap:wrap; margin-bottom:14px; }
      .nr-header h1 { margin:0 0 4px 0; font-size:1.35rem; font-weight:700; color: var(--nr-text); } .nr-header p { margin:0; font-size:0.84rem; color: var(--nr-muted); }
      .nr-actions { display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }
      .btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:6px; border:1px solid var(--nr-input-border); background: var(--ia-bg-panel, #1B212C); color: var(--nr-text); font-size:0.84rem; font-weight:600; cursor:pointer; }
      .btn:hover { filter:brightness(1.15); } .btn.primary { background: var(--nr-blue); border-color: var(--nr-blue); color: var(--ia-on-color, #fff); } .btn.sm { padding:5px 10px; font-size:0.76rem; } .btn.danger { border-color:#7f1d1d; color: var(--ia-danger-fg, #fca5a5); } .btn[disabled] { opacity:0.5; cursor:not-allowed; }
      .btn svg, .btn i, .icon-btn svg { width:14px; height:14px; } .icon-btn { background:transparent; border:none; color: var(--nr-muted); cursor:pointer; padding:4px; font-size:1rem; } .icon-btn.danger { color: var(--ia-danger-fg, #f87171); }
      .nr-meta { display:grid; grid-template-columns: repeat(5, 1fr); gap:14px; background: var(--nr-card); border:1px solid var(--nr-card-border); border-radius:8px; padding:12px 16px; margin-bottom:12px; }
      .meta { display:flex; flex-direction:column; gap:3px; } .meta .lbl { font-size:0.64rem; text-transform:uppercase; letter-spacing:0.06em; color: var(--nr-muted); } .meta .val { font-size:0.92rem; font-weight:700; color: var(--nr-text); }
      .status { display:inline-block; width:max-content; padding:3px 10px; border-radius:4px; font-size:0.7rem; font-weight:700; text-transform:uppercase; letter-spacing:0.03em; }
      .status-draft { background: rgba(250,204,21,0.18); color: var(--ia-warning-fg, #facc15); } .status-validated { background: rgba(96,165,250,0.18); color: var(--ia-info-fg, #93c5fd); } .status-signed { background: rgba(74,222,128,0.18); color: var(--ia-success-fg, #86efac); }
      .nr-notice { padding:10px 14px; border-radius:6px; font-size:0.82rem; margin-bottom:12px; background: rgba(96,165,250,0.1); border:1px solid rgba(96,165,250,0.35); color: var(--ia-info-fg, #93c5fd); }
      .nr-notice-ok { background: rgba(74,222,128,0.1); border-color: rgba(74,222,128,0.35); color: var(--ia-success-fg, #86efac); } .nr-notice-error { background: rgba(239,68,68,0.1); border-color: rgba(239,68,68,0.35); color: var(--ia-danger-fg, #fca5a5); } .nr-notice ul { margin:6px 0 0 0; padding-left:18px; }
      .nr-layout { display:grid; grid-template-columns: 200px 1fr; gap:16px; align-items:start; }
      .nr-nav { display:flex; flex-direction:column; gap:4px; position:sticky; top:12px; }
      .nr-nav-item { display:flex; align-items:center; gap:10px; padding:9px 12px; border-radius:6px; background: var(--nr-card); border:1px solid var(--nr-card-border); color: var(--nr-label); font-size:0.78rem; text-decoration:none; cursor:pointer; }
      .nr-nav-item .num { font-weight:800; font-size:0.72rem; color: var(--nr-muted); } .nr-nav-item.active { background: var(--nr-blue); border-color: var(--nr-blue); color: var(--ia-on-color, #fff); } .nr-nav-item.active .num { color: var(--ia-fg, #fff); } .nr-nav-item:hover:not(.active) { border-color: var(--nr-blue); color: var(--nr-text); }
      .nr-main { display:flex; flex-direction:column; gap:14px; min-width:0; }
      .nr-card { background: var(--nr-card); border:1px solid var(--nr-card-border); border-radius:8px; padding:16px; } .nr-context { margin-bottom:12px; }
      .card-head { display:flex; justify-content:space-between; align-items:center; gap:10px; margin-bottom:12px; flex-wrap:wrap; }
      .card-title { font-size:0.86rem; font-weight:700; color: var(--ia-info-fg, #8FB4F0); text-transform:uppercase; letter-spacing:0.03em; display:flex; align-items:center; gap:8px; } .card-num { color: var(--ia-info-fg, #8FB4F0); }
      .card-title .count { background: var(--nr-blue-soft); color: var(--ia-info-fg, #93c5fd); border-radius:10px; padding:1px 8px; font-size:0.7rem; }
      .grid { display:grid; gap:12px; } .grid.cols-2 { grid-template-columns: repeat(2, minmax(0,1fr)); } .grid.cols-3 { grid-template-columns: repeat(3, minmax(0,1fr)); } .grid.cols-4 { grid-template-columns: repeat(4, minmax(0,1fr)); } .grid.cols-5 { grid-template-columns: repeat(5, minmax(0,1fr)); }
      .field { display:flex; flex-direction:column; gap:5px; font-size:0.72rem; color: var(--nr-label); min-width:0; }
      .ctl { background: var(--nr-input); border:1px solid var(--nr-input-border); border-radius:6px; padding:8px 10px; color: var(--nr-text); font-size:0.82rem; width:100%; box-sizing:border-box; }
      .ctl:focus { outline:none; border-color: var(--nr-blue); } .ctl[readonly] { background: var(--ia-bg-panel, #121620); color: var(--ia-fg-soft, #B9C2D0); cursor:default; } .ctl:disabled { opacity:0.6; cursor:not-allowed; } .ctl.sm { padding:6px 8px; font-size:0.76rem; } .ctl.textarea { min-height:120px; resize:vertical; font-family:inherit; line-height:1.5; }
      .hint { font-size:0.68rem; color: var(--nr-muted); }
      .consumable-strip { display:grid; grid-template-columns:1fr 1fr; gap:2px 10px; margin-top:6px; padding:8px 10px; border-radius:6px; background: rgba(74,222,128,0.08); border:1px solid rgba(74,222,128,0.25); font-size:0.7rem; color: var(--nr-label); }
      .consumable-strip b { color: var(--nr-muted); font-weight:600; } .consumable-strip .validity { color: var(--ia-success-fg, #86efac); font-weight:600; } .consumable-strip.expired { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.3); } .consumable-strip.expired .validity { color: var(--ia-danger-fg, #fca5a5); }
      .asset-strip { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:4px 12px; margin-top:8px; padding:8px 10px; border-radius:6px; background: var(--ia-bg-panel, #0F131A); border:1px solid var(--nr-card-border); font-size:0.72rem; color: var(--nr-label); }
      .asset-strip b { display:block; color: var(--nr-muted); font-weight:600; font-size:0.64rem; text-transform:uppercase; } .asset-strip .warn { color: var(--ia-danger-fg, #fca5a5); }
      .table-wrap { overflow-x:auto; }
      .nr-table { width:100%; border-collapse:collapse; font-size:0.78rem; }
      .nr-table th { text-align:left; padding:9px 8px; background: var(--ia-bg-panel, #0F131A); color: var(--nr-muted); font-size:0.66rem; text-transform:uppercase; letter-spacing:0.04em; white-space:nowrap; }
      .nr-table td { padding:5px 4px; border-top:1px solid var(--nr-card-border); vertical-align:middle; color: var(--nr-text); } .nr-table td.empty { text-align:center; color: var(--nr-muted); padding:20px; }
      .cell { width:100%; box-sizing:border-box; background: var(--nr-input); border:1px solid transparent; border-radius:4px; padding:7px 8px; color: var(--nr-text); font-size:0.78rem; } .cell:focus { outline:none; border-color: var(--nr-blue); } .cell.num { text-align:center; }
      .laudo-badge { display:inline-flex; border-radius:4px; padding:2px 4px; }
      .laudo-badge.ok { background: rgba(34,197,94,0.22); } .laudo-badge.ok .laudo-select { color: var(--ia-success-fg, #86efac); } .laudo-badge.bad { background: rgba(239,68,68,0.22); } .laudo-badge.bad .laudo-select { color: var(--ia-danger-fg, #fca5a5); }
      .laudo-badge.warn { background: rgba(234,179,8,0.22); } .laudo-badge.warn .laudo-select { color: var(--ia-warning-fg, #fde047); } .laudo-badge.neutral { background: rgba(148,163,184,0.18); } .laudo-badge.neutral .laudo-select { color: var(--ia-fg, #cbd5e1); } .laudo-badge.none { background: transparent; border:1px dashed var(--nr-input-border); }
      .laudo-select { background:transparent; border:none; color: var(--nr-label); font-size:0.76rem; font-weight:700; cursor:pointer; padding:4px 2px; } .laudo-select option { background: var(--ia-bg-panel, #1B212C); color: var(--nr-text); font-weight:500; }
      .dropzone { display:flex; flex-direction:column; align-items:center; gap:8px; padding:30px 16px; border:2px dashed var(--nr-input-border); border-radius:8px; cursor:pointer; color: var(--nr-muted); text-align:center; font-size:0.82rem; }
      .dropzone.over, .dropzone:hover { border-color: var(--nr-blue); color: var(--nr-text); } .dropzone small { font-size:0.68rem; } .dropzone svg { width:22px; height:22px; }
      .attachment { display:flex; gap:16px; align-items:flex-start; } .thumb { width:220px; max-width:40%; border-radius:6px; background:#fff; object-fit:contain; cursor:zoom-in; border:1px solid var(--nr-card-border); }
      .attachment-info { display:flex; flex-direction:column; gap:6px; } .file-name { font-size:0.84rem; font-weight:600; color: var(--nr-text); word-break:break-all; } .file-meta { font-size:0.7rem; color: var(--nr-muted); } .attachment-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:4px; }
      .photo-grid { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:12px; } .photo-card { display:flex; gap:10px; background: var(--ia-bg-panel, #0F131A); border:1px solid var(--nr-card-border); border-radius:8px; padding:10px; min-width:0; }
      .photo-card .thumb { width:96px; height:96px; max-width:none; object-fit:cover; flex:none; } .photo-body { display:flex; flex-direction:column; gap:5px; min-width:0; flex:1; }
      .sig-grid { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:14px; }
      .sig-card { background: var(--ia-bg-panel, #0F131A); border:1px solid var(--nr-card-border); border-radius:8px; padding:14px; display:flex; flex-direction:column; gap:6px; align-items:center; text-align:center; } .sig-card.readonly { border-style:dashed; }
      .sig-role { font-size:0.7rem; font-weight:800; text-transform:uppercase; letter-spacing:0.05em; color: var(--nr-label); } .sig-image { height:64px; width:100%; display:flex; align-items:center; justify-content:center; background:#fff; border-radius:6px; overflow:hidden; } .sig-image img { max-height:56px; max-width:90%; object-fit:contain; }
      .sig-blank { color: var(--ia-fg-muted, #6b7280); font-size:0.68rem; font-style:italic; } .sig-name { font-size:0.86rem; font-weight:700; color: var(--nr-text); margin-top:4px; } .sig-meta { font-size:0.72rem; color: var(--nr-muted); } .sig-foot { margin-top:6px; } .sig-locked { font-size:0.7rem; color: var(--nr-muted); font-style:italic; padding:6px 0; }
      .joint-grid { display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap:12px; }
      .joint-card { position:relative; background: var(--ia-bg-panel, #0F131A); border:2px solid var(--nr-card-border); border-radius:8px; padding:12px; cursor:pointer; display:flex; flex-direction:column; gap:8px; transition: border-color .15s, box-shadow .15s; }
      .joint-card:hover { border-color: var(--nr-blue); } .joint-card.selected { border-color: var(--nr-blue); box-shadow: 0 0 0 2px var(--nr-blue-soft); background: rgba(47,111,214,0.10); } .joint-card.readonly { cursor:default; }
      .joint-head { display:flex; align-items:center; gap:8px; font-size:0.82rem; font-weight:700; color: var(--nr-text); } .joint-head .box { width:16px; height:16px; border:2px solid var(--nr-label); border-radius:3px; display:flex; align-items:center; justify-content:center; font-size:0.7rem; color: var(--ia-fg, #fff); }
      .joint-card.selected .joint-head .box { background: var(--nr-blue); border-color: var(--nr-blue); } .joint-card.selected .joint-head::after { content:"— SELECIONADO"; font-size:0.66rem; color: var(--ia-info-fg, #93c5fd); font-weight:600; letter-spacing:0.03em; }
      .joint-figure { display:flex; align-items:center; justify-content:center; padding:4px 0; } .joint-figure svg { width:100%; height:auto; max-height:150px; color: var(--nr-text); } .joint-points { font-size:0.68rem; color: var(--nr-muted); text-align:center; }
      .modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.82); display:flex; align-items:center; justify-content:center; z-index:10000; padding:20px; }
      .modal-box { background: var(--nr-card); border:1px solid var(--nr-card-border); border-radius:12px; width:100%; max-width:760px; max-height:92vh; display:flex; flex-direction:column; overflow:hidden; } .modal-box.preview { max-width:1100px; height:92vh; } .modal-box.lightbox { max-width:1000px; }
      .modal-head { padding:12px 18px; border-bottom:1px solid var(--nr-card-border); display:flex; justify-content:space-between; align-items:center; color: var(--nr-text); font-weight:600; } .modal-head span { display:flex; align-items:center; gap:8px; } .modal-body { padding:16px 18px; overflow:auto; }
      @media (max-width: 1100px) { .grid.cols-5, .grid.cols-4, .grid.cols-3 { grid-template-columns: repeat(2, minmax(0,1fr)); } .nr-meta { grid-template-columns: repeat(3, 1fr); } .photo-grid, .joint-grid { grid-template-columns: repeat(2, minmax(0,1fr)); } }
      @media (max-width: 800px) { .nr-layout { grid-template-columns: 1fr; } .nr-nav { position:static; flex-direction:row; flex-wrap:wrap; } .sig-grid, .photo-grid { grid-template-columns: 1fr; } .nr-meta { grid-template-columns: repeat(2, 1fr); } }
      @media (max-width: 560px) { .grid.cols-5, .grid.cols-4, .grid.cols-3, .grid.cols-2, .joint-grid { grid-template-columns: 1fr; } .attachment { flex-direction:column; } .thumb { max-width:100%; } }
    `;
  }
}
